/**
 * Portal voice-assistant client (ADR-0025) — the browser "edges" + the SSE pipe.
 *
 * Anthropic is the brain (server-side, via `/voice/turn`); the EARS and MOUTH
 * are browser-native here: Web Speech `SpeechRecognition` (STT) and
 * `speechSynthesis` (TTS) — zero extra keys, the fallback when no "serious"
 * provider is configured (ADR-0025). Everything feature-detects so an
 * unsupported browser degrades to a clear "not supported" instead of crashing.
 *
 * `streamVoiceTurn` POSTs the turn and parses the Server-Sent-Events stream
 * (EventSource can't POST + carry the portal token, so we read the body stream
 * ourselves) — yielding the same `delta`/`tool`/`final`/… events the backend
 * emits, so the UI can render + speak incrementally.
 */
import { usePortalAuth } from "./portalAuth";
import { consumeSse } from "./sseStream";

/** A compact, render-ready summary of a tool call (ADR-0025) — what the HUD
 *  animates: the op + table, query totals, or a write's pending/done/error. */
export interface VoicePreview {
  op?: string; // query | create | update | delete
  table?: string;
  total?: number;
  count?: number;
  columns?: string[];
  sample?: Record<string, unknown> | null;
  pending?: boolean;
  done?: boolean;
  id?: string;
  error?: string;
  tables?: number;
  /** describe_dataset — the count of visible fields on the dataset. */
  fields?: number;
}

/** The actual (RLS-scoped, capped, SQL-FREE) rows a read tool returned — echoed
 *  ONLY to the chat surface so the user can SEE what was read (anti-fabrication
 *  proof). Never carries the query itself. */
export interface VoiceToolRows {
  /** A ROW result carries columns + rows; a FILE-ONLY result (`code_execution`
   *  that produced files but no table) carries only `output_files`. Both keys are
   *  therefore optional — never deref `.rows.length` without guarding. */
  columns?: string[];
  rows?: Record<string, unknown>[];
  /** Present when the result was capped — the UI shows "… and N more". */
  total?: number;
  /** Agent-emitted chart SPEC (ADR-0071 F1a — shape only, field names of the
   *  already-masked rows; the data is `rows` verbatim, never retyped). */
  chart?: import("@/components/charts/ResultChart").ChartSpec;
  /** Files a `code_execution` tool PRODUCED (immutable-blob path): each carries a NAME
   *  + a signed, per-user download TOKEN, so a historical download stays the RIGHT file
   *  even after the workspace reused the same filename in a later turn. */
  files?: { name: string; token: string }[];
  /** LEGACY (pre-blob) produced-file NAMES, downloaded from the thread workspace by name.
   *  Kept so old persisted threads still render their download chips. Superseded by `files`. */
  output_files?: string[];
}

export type VoiceEvent =
  | { type: "delta"; text: string }
  | { type: "tool"; name: string; input?: Record<string, unknown> }
  | {
      type: "tool_result";
      name: string;
      input?: Record<string, unknown>;
      preview?: VoicePreview;
      result?: VoiceToolRows;
    }
  | { type: "final"; text: string }
  | { type: "error"; message: string }
  | { type: "done" };

export interface VoiceTurnBody {
  text: string;
  /** Client transcript — IGNORED by the server when `thread_id` is set (the
   *  server then loads history from the persisted thread, ADR-0028). */
  history: { role: "user" | "assistant"; text: string }[];
  /** Bind a persisted conversation thread: the server loads prior turns from it
   *  and persists this one. `undefined` ⇒ stateless one-shot (legacy `history`). */
  thread_id?: string | null;
  /** The node this turn hangs off (ADR-0072) — the ONE field behind fork / edit /
   *  regenerate. Omit to continue the thread's active branch; `"root"` starts a new
   *  FIRST question. The parent's ROLE decides the shape (a user node ⇒ regenerate
   *  that question, else a new question), so there's no mode flag. */
  parent_id?: string | null;
  /** Workspace paths of files attached to THIS message (ADR-0034) — passed natively
   *  to the model when its config is multimodal + the type supported, else the agent's
   *  file tools handle them. */
  attachments?: string[];
}

/** POST a turn to one of the assistant surfaces (`voice`|`chat`) and invoke
 *  `onEvent` for each SSE event until the stream ends. Both surfaces share the
 *  SAME brain + event contract (ADR-0028 Block E) — only the route differs. */
export async function streamAssistantTurn(
  slug: string,
  surface: "voice" | "chat",
  body: VoiceTurnBody,
  onEvent: (ev: VoiceEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = usePortalAuth.getState().token;
  const res = await fetch(`/api/portal/apps/${slug}/${surface}/turn`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    onEvent({ type: "error", message: `http_${res.status}` });
    return;
  }
  await consumeSse<VoiceEvent>(res.body, onEvent);
}

/** Stream a VOICE turn (`/voice/turn`). Thin wrapper over `streamAssistantTurn`. */
export function streamVoiceTurn(
  slug: string,
  body: VoiceTurnBody,
  onEvent: (ev: VoiceEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return streamAssistantTurn(slug, "voice", body, onEvent, signal);
}

/** Stream a CHAT turn (`/chat/turn`, ADR-0028 Block E) — same brain + events. */
export function streamChatTurn(
  slug: string,
  body: VoiceTurnBody,
  onEvent: (ev: VoiceEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  return streamAssistantTurn(slug, "chat", body, onEvent, signal);
}

// --- Web Speech (browser STT/TTS) ------------------------------------------

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}
interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}
interface SpeechRecognitionErrorEventLike {
  error: string; // "no-speech" | "not-allowed" | "audio-capture" | "aborted" | …
}

type SRCtor = new () => SpeechRecognitionLike;

function speechRecognitionCtor(): SRCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SRCtor;
    webkitSpeechRecognition?: SRCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** True when the browser can both LISTEN (STT) and SPEAK (TTS). */
export function speechSupported(): boolean {
  return (
    speechRecognitionCtor() !== null && typeof window !== "undefined" && "speechSynthesis" in window
  );
}

/** Start one listen → resolve with the final transcript. `onPartial` streams
 *  interim words for the live caption. Returns a stop() to cancel. */
export function listenOnce(opts: {
  lang?: string;
  onPartial?: (text: string) => void;
  onResult: (text: string) => void;
  onError?: (reason?: string) => void;
  onEnd?: () => void;
}): { stop: () => void } {
  const Ctor = speechRecognitionCtor();
  if (!Ctor) {
    opts.onError?.("unsupported");
    return { stop: () => {} };
  }
  const rec = new Ctor();
  rec.lang = opts.lang ?? (typeof navigator !== "undefined" ? navigator.language : "en-US");
  rec.continuous = false;
  rec.interimResults = true;
  rec.maxAlternatives = 1;
  let finalText = "";
  let errored = false;
  rec.onresult = (e) => {
    let interim = "";
    for (let i = 0; i < e.results.length; i++) {
      const r = e.results[i];
      const text = r[0]?.transcript ?? "";
      if (r.isFinal) finalText += text;
      else interim += text;
    }
    if (interim) opts.onPartial?.(interim);
    if (finalText) opts.onResult(finalText);
  };
  // `error` is e.g. "no-speech" | "not-allowed" | "audio-capture" | "aborted".
  // "aborted" is what we get from our own stop() — not a real failure.
  rec.onerror = (e) => {
    if (e?.error === "aborted") return;
    errored = true;
    opts.onError?.(e?.error);
  };
  rec.onend = () => {
    if (!errored) opts.onEnd?.();
  };
  rec.start();
  return { stop: () => rec.stop() };
}

let _primed = false;

/** Unlock TTS from a USER GESTURE + warm the voice list. Chrome/Safari can drop
 *  a `speak()` that isn't tied to a gesture and `getVoices()` is empty until the
 *  engine populates it async — so we kick both on the tap that starts a turn,
 *  long before the actual answer is spoken. Idempotent. */
export function primeSpeech(): void {
  if (_primed || typeof window === "undefined" || !("speechSynthesis" in window)) return;
  _primed = true;
  try {
    window.speechSynthesis.getVoices(); // trigger async population
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0; // silent warm-up
    window.speechSynthesis.speak(u);
  } catch {
    /* ignore */
  }
}

/** Pick the best installed voice for `lang` (2-letter match), else the default —
 *  forcing a `lang` with no matching voice can leave Chrome silent. */
function pickVoice(lang: string): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return undefined;
  const two = lang.slice(0, 2).toLowerCase();
  return (
    voices.find((v) => v.lang.toLowerCase().startsWith(two)) ??
    voices.find((v) => v.default) ??
    voices[0]
  );
}

/** Strip light markdown so the TTS engine doesn't READ the syntax aloud
 *  ("asterisk asterisk", "hash hash"). The model sometimes formats even on the
 *  voice surface; this is the robust last-mile fix (the prompt says "no markdown"
 *  but the model can ignore it). Keeps the words, drops the markup. */
export function stripMarkdownForSpeech(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ") // images
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links → their text
    .replace(/^\s{0,3}#{1,6}\s*/gm, "") // # headings
    .replace(/^\s{0,3}>\s?/gm, "") // > blockquotes
    .replace(/^\s*([-*+])\s+/gm, "") // - bullet markers
    .replace(/^\s*\d+\.\s+/gm, "") // 1. ordered markers
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // **bold**
    .replace(/(\*|_)(.*?)\1/g, "$2") // *italic*
    .replace(/~~(.*?)~~/g, "$2") // ~~strike~~
    .replace(/^\s*([-*_]\s*){3,}$/gm, " ") // --- horizontal rules
    .replace(/\|/g, " ") // table pipes
    .replace(/[ \t]{2,}/g, " ") // collapse runs of spaces
    .trim();
}

/** Speak `text` aloud (TTS). `onEnd` fires when finished (or on failure). */
export function speak(text: string, opts: { lang?: string; onEnd?: () => void } = {}): void {
  const clean = stripMarkdownForSpeech(text);
  if (typeof window === "undefined" || !("speechSynthesis" in window) || !clean.trim()) {
    opts.onEnd?.();
    return;
  }
  const synth = window.speechSynthesis;
  const lang = opts.lang ?? (typeof navigator !== "undefined" ? navigator.language : "en-US");
  const run = () => {
    synth.cancel(); // clear any queued/stuck utterance
    const u = new SpeechSynthesisUtterance(clean);
    const voice = pickVoice(lang);
    if (voice) u.voice = voice;
    u.lang = voice?.lang ?? lang;
    u.rate = 1.02;
    u.onend = () => opts.onEnd?.();
    u.onerror = () => opts.onEnd?.();
    synth.resume(); // Chrome occasionally leaves the engine paused after cancel()
    synth.speak(u);
  };
  // Voices populate async on Chrome — wait once if they're not ready yet.
  if (synth.getVoices().length === 0) {
    const onVoices = () => {
      synth.removeEventListener("voiceschanged", onVoices);
      run();
    };
    synth.addEventListener("voiceschanged", onVoices);
    setTimeout(() => {
      synth.removeEventListener("voiceschanged", onVoices);
      run();
    }, 250); // fallback if the event never fires
  } else {
    run();
  }
}

export function cancelSpeech(): void {
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    window.speechSynthesis.cancel();
  }
}

// --- Server-side speech (self-hosted sidecar, ADR-0025) --------------------
//
// The reliable path on desktop Chrome: record audio with MediaRecorder and POST
// it to our own backend (→ Whisper), and play TTS audio synthesised server-side
// (→ Piper). Used when branding.voice_stt/voice_tts === "server".

/** True when the browser can capture audio for server-side STT. */
export function mediaRecorderSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.MediaRecorder !== "undefined"
  );
}

export interface Recording {
  /** Stop capture and resolve with the recorded audio blob. */
  stop: () => Promise<Blob>;
}

/** Start recording from the mic. Resolve a handle whose `stop()` returns the
 *  audio blob. Rejects if permission is denied / unsupported. */
export async function startRecording(): Promise<Recording> {
  if (!mediaRecorderSupported()) throw new Error("recorder_unsupported");
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  // Prefer opus/webm; let the browser pick if unsupported.
  const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
  const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
  const chunks: BlobPart[] = [];
  rec.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  rec.start();
  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        rec.onstop = () => {
          stream.getTracks().forEach((tk) => tk.stop());
          resolve(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
        };
        rec.stop();
      }),
  };
}

/** POST a recorded clip to the server STT endpoint → transcript text. */
export async function transcribeViaServer(slug: string, audio: Blob): Promise<string> {
  const token = usePortalAuth.getState().token;
  const fd = new FormData();
  fd.append("audio", audio, "audio.webm");
  const res = await fetch(`/api/portal/apps/${slug}/voice/stt`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: fd,
  });
  if (!res.ok) throw new Error(`stt_http_${res.status}`);
  const data = (await res.json()) as { text?: string };
  return (data.text ?? "").trim();
}

/** Fetch server-synthesised speech and play it. Resolves when playback ends
 *  (or fails). `onEnd` mirrors the browser `speak()` contract for the caller. */
export async function speakViaServer(
  slug: string,
  text: string,
  opts: { lang?: string; onEnd?: () => void } = {},
): Promise<void> {
  const clean = stripMarkdownForSpeech(text);
  if (!clean.trim()) {
    opts.onEnd?.();
    return;
  }
  const token = usePortalAuth.getState().token;
  try {
    const res = await fetch(`/api/portal/apps/${slug}/voice/speak`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text: clean, lang: opts.lang }),
    });
    if (!res.ok) throw new Error(`tts_http_${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.onended = () => {
      URL.revokeObjectURL(url);
      opts.onEnd?.();
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      opts.onEnd?.();
    };
    await audio.play();
  } catch {
    opts.onEnd?.(); // surface as "done" so the UI returns to idle
  }
}
